// Autocomplete for choosing a task's parent in the hierarchy.
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { generateBreadcrumbPath } from '@/features/tasks/utils/structure/taskHierarchy';
import { useT } from '@/i18n/useT';
import { autocompleteTextFieldSx } from '@/features/tasks/components/forms/taskFormStyles';

/**
 * @param {Object} props
 * @param {import('@/types').Task} props.task
 * @param {Array<{id: string|null, title: string}>} props.parentTaskOptions
 * @param {Map<string, import('@/types').Task>} props.tasksMap
 * @param {(event: unknown, newValue: {id: string|null, title: string}|null) => void} props.onParentChange
 */
const TaskParentSelector = ({
  task,
  parentTaskOptions,
  tasksMap,
  onParentChange,
}) => {
  const t = useT();

  const rootOption = useMemo(
    () =>
      parentTaskOptions.find((option) => option.id === null) ?? {
        id: null,
        title: t('taskform.rootParent', 'Hoofdniveau'),
      },
    [parentTaskOptions, t],
  );

  const selectedOption = useMemo(() => {
    if (!task?.parentId) return rootOption;
    return (
      parentTaskOptions.find((option) => option.id === task.parentId) ??
      rootOption
    );
  }, [task?.parentId, parentTaskOptions, rootOption]);

  return (
    <Autocomplete
      fullWidth
      value={selectedOption}
      onChange={onParentChange}
      options={parentTaskOptions}
      getOptionLabel={(option) => option?.title || ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t(
            'taskparentselector.placeholder',
            'Kies bovenliggende taak',
          )}
          variant="outlined"
          inputProps={{
            ...params.inputProps,
            'aria-label': t('taskform.parentLabel', 'Bovenliggende taak'),
          }}
          sx={autocompleteTextFieldSx}
        />
      )}
      renderOption={(props, option) => {
        const path = option.id ? generateBreadcrumbPath(option, tasksMap) : [];
        const rootParentHint = t(
          'taskform.rootParentHint',
          'Deze taak heeft geen bovenliggende taak.',
        );
        return (
          <li
            {...props}
            key={String(option.id ?? 'root')}
            title={option.id === null || option.id === undefined ? rootParentHint : undefined}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {option.title}
              </Typography>
              {path.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                >
                  {path.map((item) => item.title).join(' › ')}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }}
    />
  );
};

export default TaskParentSelector;
